/**
 * Who is allowed to change what, by role (JBWEB-1213).
 *
 * The admin app is reached by 500, the agency managers, 515, the
 * collaborators working alongside them, 510, the Commercials (JBWEB-1415),
 * 520, the recruiters (JBWEB-1416), and 525, who only sees the missions (their names on screen are
 * ADMIN_ROLE_LABELS). All clear the `role >= 500` gate every admin endpoint
 * checks, so that gate says "may use the admin app", not "may change anything
 * in it".
 *
 * Settings that decide what a client is billed are the second kind, and 500
 * exactly is what separates them.
 */
export const SUPER_ADMIN_ROLE = 500;
export const COMMERCIAL_ROLE = 510;
export const COLLABORATOR_ROLE = 515;
export const RECRUITER_ROLE = 520;
// The roles that work in the whole admin app (525 only sees the missions).
// 510 and 520 are left out on purpose: they only get what the predicates
// below grant them, not everything 515 has.
export const STAFF_ROLES = [SUPER_ADMIN_ROLE, COLLABORATOR_ROLE];

// How the navbar, the profile page and the admin console name each role.
export const ADMIN_ROLE_LABELS = {
  [SUPER_ADMIN_ROLE]: 'Responsable d’agence',
  [COMMERCIAL_ROLE]: 'Commercial',
  [COLLABORATOR_ROLE]: 'Collaborateur',
  [RECRUITER_ROLE]: 'Chargé(e) de recrutement',
};

// The recruiter has the Commercial's rights outside the CRM, prospecting
// excepted.
const STAFF_AND_COMMERCIAL_ROLES = [
  ...STAFF_ROLES,
  COMMERCIAL_ROLE,
  RECRUITER_ROLE,
];

// Tasks, companies and planning.
export const canWorkWithCompanies = (role) =>
  STAFF_AND_COMMERCIAL_ROLES.includes(role);

// Candidates, pools and identity validation. Reviews and qualifications stay
// with STAFF_ROLES.
export const canWorkWithCandidates = (role) =>
  STAFF_AND_COMMERCIAL_ROLES.includes(role);

// Prospecting companies in the CRM is the Commercial's job alone: neither the
// agency manager nor any other admin role prospects (decision of 22/10/2026,
// as in the prototype).
export const canProspect = (role) => role === COMMERCIAL_ROLE;

// The roles that do not prospect lose the prospecting call list on screen:
// the home row, « Ajouter à ma liste d’appels », the callback « Appeler ».
export const hidesProspection = (role) => !canProspect(role);

// The « Appels du jour » card of the home page.
export const showsCallsOnHome = (role) => canProspect(role);

export const isSuperAdmin = (userData) =>
  userData?.role === SUPER_ADMIN_ROLE;

const isAdminAppRole = (role) =>
  typeof role === 'number' && role >= 500 && role < 600;

/**
 * Generating a contract on BeSTT, updating it and sending it for signature:
 * role 500, or an admin-app user granted `canGenerateAndSignContracts` in the
 * database (no screen writes it). Twin of `canGenerateAndSignContracts` in
 * api/utils/roles.js, which is the actual guard.
 */
export const canGenerateAndSignContracts = (userData) =>
  isSuperAdmin(userData) ||
  (isAdminAppRole(userData?.role) &&
    userData?.canGenerateAndSignContracts === true);

export default isSuperAdmin;
